import * as React from 'react'

import { Section } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import employees from 'modules/employees/store/collections/employees'
import ConfirmationModal from 'modules/job_catalog/components/ConfirmationModal'
import DraftLevelModal from 'modules/job_catalog/components/DraftLevelModal'
import RoleLevelsSortableList from 'modules/job_catalog/components/RoleLevelSortableList'
import useDestroyLevel from 'modules/job_catalog/hooks/useDestroyLevel'
import { Level, useRoleLevels } from 'modules/job_catalog/hooks/useRoleLevels'
import { employeeJobLevel } from 'modules/job_catalog/store/collections/levels'

interface Props {
  roleId: number
  contractVersions: ContractVersions
}

const RoleLevelsForm: React.FC<Props> = ({ roleId, contractVersions }) => {
  const [levels, isFetching, refetch] = useRoleLevels(roleId)
  const destroyLevel = useDestroyLevel()
  const [draftLevel, setDraftLevel] = React.useState<Level>()
  const [nextDeletedLevel, setNextDeletedLevel] = React.useState<Level>()
  const [isDraftOpen, setDraftIsOpen] = React.useState(false)
  const [modalDescription, setModalDescription] = React.useState(
    i18n.t('job_catalog.remove_level_confirmation_description')
  )
  const {
    createJobRoleNewLevelStart,
    editJobRoleLevelCancel,
    archiveJobRoleLevelSuccess,
    archiveJobRoleLevelCancel,
  } = trackingEvents.jobCatalog

  const handleNewLevel = () => {
    setDraftIsOpen(true)
    track(createJobRoleNewLevelStart)
  }

  const newLevelAction = {
    label: i18n.t('job_catalog.job_levels_action'),
    onClick: handleNewLevel,
  }

  const description = i18n.t('job_catalog.job_levels_description')

  const handleDraftModalClose = () => {
    setDraftLevel(undefined)
    setDraftIsOpen(false)
    refetch()
    track(editJobRoleLevelCancel)
  }

  const handleDeleteAction = (level: Level) => {
    const employeesWithRole = employees.filter(
      (x) => employeeJobLevel(contractVersions, x)?.attributes.id === level.id
    )

    if (employeesWithRole.length) {
      setModalDescription(
        i18n.t('job_catalog.reassign_level_confirmation_description', {
          employees: employeesWithRole.length,
        })
      )
    }

    setNextDeletedLevel(level)
  }

  const handleDeleteLevel = async (level: Level) => {
    const result = await destroyLevel(level.id)
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      refetch()
      setNextDeletedLevel(undefined)
      track(archiveJobRoleLevelSuccess)
    }
  }

  return (
    <Section.List
      illustration="automations"
      title={i18n.t('job_catalog.job_levels_title')}
      description={description}
      action={newLevelAction}
    >
      <RoleLevelsSortableList
        levels={levels}
        isFetching={isFetching}
        handleEdit={setDraftLevel}
        handleDelete={(level) => handleDeleteAction(level)}
        onUpdated={refetch}
      />

      {(draftLevel || isDraftOpen) && (
        <DraftLevelModal draft={draftLevel} roleId={roleId} onClose={handleDraftModalClose} />
      )}
      {nextDeletedLevel && (
        <ConfirmationModal
          illustration="archive"
          title={i18n.t('job_catalog.remove_level_confirmation_title')}
          description={modalDescription}
          onConfirm={() => handleDeleteLevel(nextDeletedLevel)}
          onCancel={() => {
            setNextDeletedLevel(undefined)
            track(archiveJobRoleLevelCancel)
          }}
        />
      )}
    </Section.List>
  )
}

export default observer(RoleLevelsForm)
